import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Mail,
  Save,
  Loader2,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  TestTube
} from 'lucide-react';
import api from '@/services/api';
import { safeHtml } from '@/lib/sanitize';

// Help Button component
const HelpButton = ({ title, content }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="relative">
      <Button variant="ghost" size="sm" onClick={() => setIsOpen(!isOpen)} className="text-slate-500 hover:text-slate-700">
        <span className="w-5 h-5 rounded-full border border-slate-300 flex items-center justify-center text-xs">?</span>
      </Button>
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 p-4 bg-white border border-slate-200 rounded-lg shadow-lg z-50">
          <h4 className="font-medium text-slate-900 mb-2">{title}</h4>
          <div className="text-sm text-slate-600" dangerouslySetInnerHTML={safeHtml(content)} />
          <Button size="sm" variant="outline" onClick={() => setIsOpen(false)} className="mt-3 w-full">Close</Button>
        </div>
      )}
    </div>
  );
};

export const AdminSmtpTab = () => {
  const [smtpConfig, setSmtpConfig] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    from_email: '',
    from_name: 'Harmonix',
    use_tls: true,
    use_ssl: false
  });
  const [existingSmtpConfig, setExistingSmtpConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [smtpHealth, setSmtpHealth] = useState(null);

  useEffect(() => {
    loadSmtpConfig();
    loadSmtpHealth();
  }, []);

  const loadSmtpHealth = async () => {
    // Never let this call break the tab — the health endpoint is diagnostic.
    try {
      const r = await api.get('/admin/smtp/health');
      setSmtpHealth(r?.data || null);
    } catch (_e) {
      setSmtpHealth(null);
    }
  };

  const loadSmtpConfig = async () => {
    try {
      const response = await api.get('/admin/smtp');
      const d = response?.data;
      // Backend returns null when no config exists.
      if (d && typeof d === 'object' && d.host) {
        setExistingSmtpConfig(d);
        setSmtpConfig({
          host: d.host || '',
          port: typeof d.port === 'number' ? d.port : 587,
          username: d.username || '',
          password: '',
          from_email: d.from_email || '',
          from_name: d.from_name || 'Harmonix',
          use_tls: d.use_tls ?? true,
          use_ssl: d.use_ssl ?? false,
        });
      } else {
        // No SMTP config yet — keep the form in its initial empty state.
        setExistingSmtpConfig(null);
      }
    } catch (error) {
      // Never let a load failure blank the page — log and stay on the form.
      console.error('Failed to load SMTP config:', error);
      setExistingSmtpConfig(null);
    }
  };

  const handleSaveSmtp = async () => {
    setSaving(true);
    try {
      // If user did not type a new password, omit it so the backend keeps the existing one.
      const { password: pwd, ...rest } = smtpConfig;
      let payload;
      if (existingSmtpConfig && !pwd) {
        payload = rest;
      } else if (!existingSmtpConfig && !pwd) {
        toast.error('Password is required for first-time SMTP setup');
        setSaving(false);
        return;
      } else {
        payload = smtpConfig;
      }
      await api.post('/admin/smtp', payload);
      toast.success('SMTP configuration saved');
      loadSmtpConfig();
      // Refresh delivery health so the banner clears (or updates) as soon as
      // the new credentials start succeeding.
      loadSmtpHealth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SMTP configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    setTestingEmail(true);
    try {
      const res = await api.post('/admin/smtp/test', { email: testEmail });
      // Backend returns 200 with { success: true/false, message, suggestions[] }
      if (res.data?.success) {
        const note = res.data?.delivery_note;
        toast.success(
          note ? `Test email accepted by SMTP. ${note}` : `Test email sent to ${testEmail}`,
          { duration: 14000 }
        );
      } else {
        const reason = res.data?.message || 'Test email failed to send';
        const suggestions = res.data?.suggestions || [];
        toast.error(
          suggestions.length
            ? `${reason}\n\n${suggestions.slice(0, 4).join('\n')}`
            : reason,
          { duration: 12000 }
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
      // Refresh delivery health so the banner reflects the latest test.
      loadSmtpHealth();
    }
  };

  return (
    <Card className="bg-white border-slate-200 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            SMTP Configuration
          </CardTitle>
          <CardDescription className="text-slate-600">
            Configure email delivery for notifications
          </CardDescription>
        </div>
        <HelpButton 
          title="SMTP Configuration" 
          content={`
            <p>Set up your email server:</p>
            <ul class="mt-2 space-y-1">
              <li><strong>Gmail:</strong> smtp.gmail.com, Port 587, TLS</li>
              <li><strong>Outlook:</strong> smtp.office365.com, Port 587</li>
              <li><strong>SendGrid:</strong> smtp.sendgrid.net, Port 587</li>
            </ul>
            <p class="mt-2 text-amber-600">For Gmail, use an App Password (not your regular password).</p>
          `} 
        />
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Live delivery health — surfaced above every other block so admins
            can't miss when the stored credentials are being rejected. */}
        {smtpHealth?.recommendation && (
          <div
            className={
              smtpHealth.recommendation.severity === 'critical'
                ? 'p-4 rounded-lg border-2 border-red-300 bg-red-50 space-y-2'
                : 'p-4 rounded-lg border-2 border-amber-300 bg-amber-50 space-y-2'
            }
            data-testid="smtp-health-banner"
          >
            <div className="flex items-start gap-3">
              <AlertCircle
                className={
                  smtpHealth.recommendation.severity === 'critical'
                    ? 'w-5 h-5 text-red-600 mt-0.5 flex-shrink-0'
                    : 'w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0'
                }
              />
              <div className="flex-1 min-w-0">
                <p className={
                  smtpHealth.recommendation.severity === 'critical'
                    ? 'text-sm font-semibold text-red-900'
                    : 'text-sm font-semibold text-amber-900'
                }>
                  {smtpHealth.recommendation.title}
                </p>
                <p className={
                  smtpHealth.recommendation.severity === 'critical'
                    ? 'text-xs text-red-800 mt-1 leading-relaxed'
                    : 'text-xs text-amber-800 mt-1 leading-relaxed'
                }>
                  {smtpHealth.recommendation.detail}
                </p>
                <p className="text-xs text-slate-700 mt-2">
                  <strong>Last 24h:</strong>{' '}
                  <span className="text-red-700">{smtpHealth.failed} failed</span>
                  {' · '}
                  <span className="text-emerald-700">{smtpHealth.sent} sent</span>
                  {smtpHealth.bad_credentials_failures > 0 && (
                    <>
                      {' · '}
                      <span>{smtpHealth.bad_credentials_failures} due to BadCredentials</span>
                    </>
                  )}
                </p>
                {smtpHealth.recommendation.docs_url && (
                  <a
                    href={smtpHealth.recommendation.docs_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-red-700 hover:text-red-900 underline underline-offset-2 mt-2"
                    data-testid="smtp-health-docs-link"
                  >
                    Open Google App Passwords &rarr;
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
          <p className="text-sm font-semibold text-blue-900">What is this SMTP used for?</p>
          <p className="text-xs text-blue-800 leading-relaxed">
            This SMTP server sends <strong>Harmonix system emails only</strong> — email verification, password resets, login alerts, affiliate payouts and admin notifications. It is <strong>not used by your customers&apos; AI Employees</strong> to reply to inbound emails or run automations.
          </p>
          <p className="text-xs text-blue-800 leading-relaxed">
            Each customer connects their own mailbox to each AI Employee via <em>AI Employee → Channels → Email</em> (Gmail OAuth or IMAP/SMTP). The AI Employee then sends replies from <strong>the customer&apos;s own email address</strong>, keeping Harmonix&apos;s own email-sending costs at zero.
          </p>
        </div>

        {existingSmtpConfig && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-700 text-sm">SMTP configured: {existingSmtpConfig.host}</span>
          </div>
        )}
        
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-700">SMTP Host</Label>
            <Input 
              placeholder="smtp.gmail.com" 
              value={smtpConfig.host} 
              onChange={(e) => setSmtpConfig({ ...smtpConfig, host: e.target.value })} 
              maxLength={120}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400" 
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700">Port</Label>
            <Input 
              type="number" 
              value={smtpConfig.port} 
              onChange={(e) => {
                const port = parseInt(e.target.value) || 587;
                // Auto-sync TLS/SSL toggles to the well-known port conventions
                // so admins never end up with a mismatch (the cause of the
                // [SSL: WRONG_VERSION_NUMBER] error).
                let { use_tls, use_ssl } = smtpConfig;
                if (port === 465) { use_tls = false; use_ssl = true; }
                else if (port === 587) { use_tls = true; use_ssl = false; }
                else if (port === 25) { use_tls = false; use_ssl = false; }
                setSmtpConfig({ ...smtpConfig, port, use_tls, use_ssl });
              }} 
              min={1}
              max={65535}
              className="bg-white border-slate-300 text-slate-900" 
            />
            <p className="text-xs text-slate-500">
              <strong>587</strong> = STARTTLS (Gmail, Outlook, SendGrid). <strong>465</strong> = implicit SSL. <strong>25</strong> = plain.
            </p>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700">Username</Label>
            <Input 
              value={smtpConfig.username} 
              onChange={(e) => setSmtpConfig({ ...smtpConfig, username: e.target.value })} 
              maxLength={256}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400" 
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700">
              Password / App Password
              {existingSmtpConfig && <span className="ml-2 text-xs text-slate-500">(leave blank to keep the stored one)</span>}
            </Label>
            <div className="relative">
              <Input 
                type={showPassword ? "text" : "password"} 
                value={smtpConfig.password} 
                onChange={(e) => setSmtpConfig({ ...smtpConfig, password: e.target.value })} 
                placeholder={existingSmtpConfig ? '••••••••  (kept secure on the server)' : '16-character Gmail App Password'}
                maxLength={256}
                className="bg-white border-slate-300 text-slate-900 pr-10"
                data-testid="smtp-password-input"
              />
              <button 
                type="button" 
                onClick={() => setShowPassword(!showPassword)} 
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              <strong>Gmail users:</strong> use an <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline">App Password</a> (16 chars), not your regular Google password. Regular passwords are rejected by Gmail SMTP.
            </p>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700">From Email</Label>
            <Input 
              type="email" 
              value={smtpConfig.from_email} 
              onChange={(e) => setSmtpConfig({ ...smtpConfig, from_email: e.target.value })} 
              maxLength={256}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400" 
            />
            {smtpConfig.host?.toLowerCase().includes('gmail') &&
             smtpConfig.username && smtpConfig.from_email &&
             smtpConfig.from_email.toLowerCase() !== smtpConfig.username.toLowerCase() && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                <strong>Gmail tip:</strong> Gmail silently drops mail when the &quot;From&quot; differs from the SMTP username. We&apos;ll automatically override From to <code>{smtpConfig.username}</code> and route replies to <code>{smtpConfig.from_email}</code>. For a deliverable branded sender, use SendGrid / Postmark / Amazon SES instead.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700">From Name</Label>
            <Input 
              value={smtpConfig.from_name} 
              onChange={(e) => setSmtpConfig({ ...smtpConfig, from_name: e.target.value })} 
              maxLength={80}
              className="bg-white border-slate-300 text-slate-900" 
            />
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Switch 
              checked={smtpConfig.use_tls} 
              onCheckedChange={(c) => setSmtpConfig({ ...smtpConfig, use_tls: c })} 
            />
            <Label className="text-slate-700">Use TLS</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch 
              checked={smtpConfig.use_ssl} 
              onCheckedChange={(c) => setSmtpConfig({ ...smtpConfig, use_ssl: c })} 
            />
            <Label className="text-slate-700">Use SSL</Label>
          </div>
        </div>
        
        <div className="flex items-center gap-4 pt-4 border-t border-slate-200">
          <Button type="button" onClick={handleSaveSmtp} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="smtp-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Configuration
          </Button>
          <div className="flex items-center gap-2 ml-auto">
            <Input 
              type="email" 
              placeholder="test@example.com" 
              value={testEmail} 
              onChange={(e) => setTestEmail(e.target.value)} 
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 w-64" 
            />
            <Button 
              type="button"
              variant="outline" 
              onClick={handleTestSmtp} 
              disabled={testingEmail || !existingSmtpConfig} 
              className="border-slate-300 text-slate-700"
              data-testid="smtp-test-btn"
            >
              {testingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <TestTube className="w-4 h-4 mr-2" />}
              Send Test
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminSmtpTab;
